import type {ReactElement} from 'react';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import styles from './transitionable-panel-v2.module.scss';
import type {TransitionablePanelV2Props} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {AnimatePresence, motion, type Variants} from 'framer-motion';
import useWindowSize from '@Hooks/useWindowSize';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Panel} from '@Components/panel';
import LoadingPanel from '@Panels/loading-panel/loading-panel';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {noop} from '@Utils/general.util';

export function TransitionablePanelV2({
  panelId,
  content,
  header,
  contentKey,
  transitionDirection,
  videoURL,
  imageURL,
  imageSrcSet,
  imageClassName,
  imageAlt,
  rightColumn,
  modalId,
  loadingText = window.i18next.t('pmwjs_loading'),
  loadingContent,
  isLoading = false,
  isCloseIconDisabled = false,
  hideCloseIcon = false,
  isScrollable = false,
  closeIconClassName = '',
  hideSeeMorePill = false,
  animateHeightOnResize = false,
  growOnlyWithinStep = false,
  closeIconShape = IconShape.SQUARE,
  onModalClose = noop,
}: TransitionablePanelV2Props): ReactElement {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const {windowWidth} = useWindowSize();
  const showRightColumn = windowWidth > 900;
  const isMobile = windowWidth <= 780;
  const drawsSeeMorePill = isScrollable && !hideSeeMorePill;

  const swipeVariants: Variants = {
    enter: (dir: TransitionDirection) => ({
      opacity: 0,
      x: dir === TransitionDirection.FORWARD ? 50 : -50,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: TransitionDirection) => ({
      opacity: 0,
      x: dir === TransitionDirection.BACKWARD ? 50 : -50,
    }),
  };

  function getLeftColumnContent(): ReactElement | null {
    if (content === null) {
      return content;
    }

    return (
      <AnimatePresence initial={false} mode="wait" custom={transitionDirection}>
        <motion.div
          className={`_full-height ${getGraphic() === null ? styles.singleColumnContainer : styles.columnContainer}`}
          key={contentKey}
          custom={transitionDirection}
          variants={swipeVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: {type: 'spring', stiffness: 600, damping: 30},
            opacity: {duration: 0.1},
          }}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    );
  }

  function getGraphic(): ReactElement | null {
    if (!showRightColumn) {
      return null;
    }

    if (rightColumn !== undefined) {
      return rightColumn;
    }

    const className = `_full-height ${styles.graphic}`;
    if (videoURL !== undefined) {
      return <video muted autoPlay loop playsInline src={videoURL} className={className} />;
    }

    if (imageURL !== undefined) {
      return <img src={imageURL} srcSet={imageSrcSet} className={`${className} ${imageClassName ?? ''}`} alt={imageAlt ?? ''} />;
    }

    return null;
  }

  function getRightColumnContent(): ReactElement | null {
    const graphic = getGraphic();
    if (graphic === null) {
      return null;
    }
    return <div className={`_full-height ${styles.columnContainer}`}>{graphic}</div>;
  }

  function onClickClose(): void {
    onModalClose();
    if (modalId !== undefined) {
      window.PMW.redux.store.dispatch(closeModal(modalId));
    }
  }

  function getCloseIcon(): ReactElement | null {
    if (hideCloseIcon || isMobile) {
      return null;
    }

    return (
      <div className={`${styles.closeIconContainer} ${closeIconClassName}`}>
        <Icon
          icon="icon-close"
          size={IconSize.SIZE_ICON_24}
          type={IconType.DARK}
          shape={closeIconShape}
          onClick={onClickClose}
          className={styles.closeIcon}
          isDisabled={isCloseIconDisabled}
        />
      </div>
    );
  }

  function getContent(): ReactElement {
    const leftColumnContent = getLeftColumnContent();
    const rightColumnContent = getRightColumnContent();
    if (isLoading || leftColumnContent === null) {
      if (loadingContent !== undefined && loadingContent !== null) {
        return loadingContent;
      }
      return (
        <div className={rightColumnContent === null ? styles.wideLoader : styles.loader}>
          <LoadingPanel text={loadingText} loaderSize={LOADER_SIZE.LARGE} />
        </div>
      );
    }

    return (
      <>
        {leftColumnContent}
        {rightColumnContent}
      </>
    );
  }

  const ref = useRef<HTMLDivElement>(null);
  const measuredStepRef = useRef<string | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null) {
      return;
    }

    const handleScroll = (): void => {
      setIsScrolledToBottom(el.scrollHeight - el.scrollTop <= el.clientHeight + 1);
    };

    el.addEventListener('scroll', handleScroll);
    return (): void => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    ref.current?.scrollTo({top: 0});
  }, [contentKey]);

  /**
   * The panel is what caps its own content, so the height the content wants is only readable while that cap is off.
   * The release has to happen with transitions suppressed and be committed before the real height is written, or the
   * browser takes the released height as the transition's starting point and there is nothing left to animate.
   * It also has to happen top-aligned: the panel centres its content vertically, so content taller than the released
   * panel would overflow upward as well as downward, the measurement would miss the upper half, and the browser would
   * clamp the scroll position to that shrunken range.
   */
  const applyContentHeight = useCallback((): void => {
    const el = ref.current;
    if (el === null) {
      return;
    }

    const currentHeight = el.style.height;
    el.style.transition = 'none';
    el.style.alignItems = 'flex-start';
    el.style.height = 'auto';
    const naturalHeight = `${el.scrollHeight}px`;
    el.style.height = currentHeight;
    el.style.alignItems = '';
    el.getBoundingClientRect();
    el.style.transition = '';

    if (currentHeight === naturalHeight) {
      return;
    }

    /**
     * Arriving at a step is the one moment the panel may take any height; after that it may only
     * grow. Stripe's embedded form settles by shrinking a little for customers who already have a
     * card on file, and following that reads as the dialog resizing itself while the user looks at it.
     */
    const isArrivingAtStep = measuredStepRef.current !== contentKey;
    measuredStepRef.current = contentKey;
    if (!isArrivingAtStep && growOnlyWithinStep && parseFloat(naturalHeight) < parseFloat(currentHeight)) {
      return;
    }

    el.style.height = naturalHeight;
  }, [contentKey, growOnlyWithinStep]);

  useLayoutEffect(() => {
    if (animateHeightOnResize) {
      applyContentHeight();
    }
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!animateHeightOnResize || el === null) {
      return undefined;
    }

    /**
     * Two changes never reach a render of this component: Stripe's embedded form sizes its own iframe long after
     * React is done, and the step transition swaps the content from inside `AnimatePresence`. Watching the panel
     * itself catches both — as long as the height this effect writes on that same panel is not read back as one.
     */
    let scheduledMeasurement = 0;
    const observer = new MutationObserver((records): void => {
      if (records.every((record) => record.target === el && record.type === 'attributes')) {
        return;
      }
      window.cancelAnimationFrame(scheduledMeasurement);
      scheduledMeasurement = window.requestAnimationFrame(applyContentHeight);
    });
    observer.observe(el, {attributes: true, characterData: true, childList: true, subtree: true});

    return (): void => {
      window.cancelAnimationFrame(scheduledMeasurement);
      observer.disconnect();
    };
  }, [animateHeightOnResize, applyContentHeight]);

  function onClickSeeMore(): void {
    if (ref.current === null) {
      return;
    }

    ref.current.scrollTo({
      top: ref.current.scrollHeight,
      behavior: 'smooth',
    });
  }

  function getSeeMorePill(): ReactElement | null {
    if (isLoading || !drawsSeeMorePill || isScrolledToBottom) {
      return null;
    }

    return (
      <div className={styles.seeMorePillContainer}>
        <ClickableDiv
          onClick={onClickSeeMore}
          className={`flex-row-align-center radius-round spacing-m-b-4 spacing-p-t-2 spacing-p-b-2 spacing-p-l-4 spacing-p-r-4 hover-transition-all ${styles.seeMorePill}`}
        >
          <Text val={window.i18next.t('pmwjs_see_more')} size={TextSize.XSMALL} bold className="_unmargin-bottom spacing-m-r-1 content-body" />
          <Icon icon="icon-caret-down" type={IconType.GHOST_UNCOLORED} size={IconSize.SIZE_ICON_16} className={styles.seeMoreIcon} />
        </ClickableDiv>
      </div>
    );
  }

  /**
   * Transitioning the panel's own height, rather than framer-motion's transform-based `layout`, is what lets the modal
   * box around it follow: the modal hugs its content, so a real height change carries it along and a scaled one does not.
   * The `_full-height` helper is dropped while it does, because its `!important` would outrank the height being animated.
   */
  function getPanelContent(): ReactElement<any> {
    return (
      <div className="_full-height">
        {getCloseIcon()}
        <div
          className={`flex-row-align-center ${animateHeightOnResize ? styles.animatedHeight : '_full-height'} ${styles.panelContainer} ${
            isScrollable ? styles.scrollable : ''
          } ${drawsSeeMorePill ? styles.seeMorePillOverlap : ''}`}
          ref={ref}
        >
          {getContent()}
        </div>
        {getSeeMorePill()}
      </div>
    );
  }

  return <Panel panelId={panelId} panelContent={getPanelContent()} panelHeader={header} />;
}
